import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';

const PEOPLE_API_BASE: string = process.env.VITE_PEOPLE_API_URL || 'http://localhost:4000';

type PeopleEventType =
  | 'leave_submitted'
  | 'leave_decided'
  | 'expense_submitted'
  | 'expense_decided'
  | 'workflow_updated'
  | 'notification';

interface PeopleEvent {
  type: PeopleEventType;
  refId?: string;
  refType?: string;
}

// Which cached query-key prefixes to refresh for each push. Partial keys
// (e.g. ['expenses']) invalidate every query starting with that prefix —
// react-query matches by prefix, so this covers both the 'my expenses' and
// 'all expenses' variants without knowing the exact employeeId in play.
const INVALIDATIONS: Record<PeopleEventType, string[][]> = {
  leave_submitted: [['leaveRequests'], ['notifications']],
  leave_decided: [['leaveRequests'], ['leaveBalances'], ['notifications']],
  expense_submitted: [['expenses'], ['allExpenses'], ['notifications']],
  expense_decided: [['expenses'], ['allExpenses'], ['notifications']],
  workflow_updated: [['workflowInstances']],
  notification: [['notifications']],
};

/**
 * Holds one persistent Server-Sent-Events connection open for the lifetime
 * of the app shell, so leave/expense/workflow decisions show up live instead
 * of only refreshing on the acting user's own mutation (see sk-people-api's
 * eventStream.service.ts for the publish side). The browser's EventSource
 * reconnects automatically on drop, so no manual retry logic is needed here.
 */
export function useLiveEvents(queryClient: QueryClient, enabled: boolean) {
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    const source = new EventSource(`${PEOPLE_API_BASE}/api/people/events/stream`, { withCredentials: true });

    source.onmessage = (e) => {
      let event: PeopleEvent;
      try {
        event = JSON.parse(e.data);
      } catch {
        return;
      }
      for (const key of INVALIDATIONS[event.type] ?? []) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    };

    return () => source.close();
  }, [queryClient, enabled]);
}
